import { STATUS_BY_KEY, BOOKING_STATUSES } from '../bookings/constants';
import type { BookingStatus } from '../bookings/types';
import { Icon } from './Icon';

// Label, tone and glyph — everything the pill actually needs. BOOKING_STATUSES
// and VIEWING_STATUS_RECORDS are both lists of this shape, which is what lets
// one pill serve two vocabularies without learning about either.
export interface StatusInfo {
  label: string;
  tone: string;
  icon: string;
}

export interface StatusPillProps {
  // A booking status, resolved internally through BOOKING_STATUSES. Optional
  // only because `record` is the other way in.
  status?: BookingStatus;
  // AN ALREADY-RESOLVED RECORD, for a vocabulary that is not the booking one.
  // The pill's job is label + tone + icon; which vocabulary they came from is
  // the caller's business. A viewing's statuses are deliberately their own set
  // — sharing one with bookings is how the attention rail double-counts.
  //
  // `tone` is applied VERBATIM as a CSS class, so a new vocabulary must ship
  // its own tone rules; borrowing `unconfirmed` would paint a viewing the
  // booking-status gold.
  record?: StatusInfo;
  // GLYPH ONLY. The icon, the tone, a title for a pointer and an aria-label
  // for a reader; no word element at all — a visually-hidden one still
  // reports a scrollWidth, and the rail's clip contract reads that as text
  // being cut. For a row with no room for a word — the bookings rail, where
  // the pill's 86px had a line to itself (Freddi, 2026-10-06).
  glyph?: boolean;
  // Read-only when omitted — the pill renders as a disabled badge. When given,
  // the pill is an interactive control that cycles the status; the click is
  // stopped from bubbling so it never also triggers the row's own click.
  onClick?: () => void;
  // Extra class(es) for callers that hook the pill (e.g. the modal's
  // bd-status-trigger). The shared label + tone are unchanged.
  className?: string;
  // Override the hover title (defaults to the cycle hint when interactive).
  title?: string;
  // Temporarily non-interactive while its own write is in flight — distinct from
  // the read-only badge (no onClick): the pill stays a control, just briefly
  // un-clickable so a rapid second press can't race the first. `is-busy` marks
  // the transient state so it can be styled apart from a read-only badge.
  disabled?: boolean;
}

// The single status display for the whole app — label + tone resolve from
// BOOKING_STATUSES so every page and modal reads the same names. Used by the
// bookings table, the booking modal, and anywhere a status is shown.
export function StatusPill({ status, record, onClick, className, title, disabled, glyph = false }: StatusPillProps) {
  const info: StatusInfo = record ?? (status ? STATUS_BY_KEY[status] : undefined) ?? BOOKING_STATUSES[0]!;
  const interactive = !!onClick;
  const off = disabled || !interactive; // read-only badge OR in-flight control
  // A READ-ONLY GLYPH IS NOT A BUTTON. As a disabled <button> it had
  // `pointer-events: none` (so clicks pass through to the row), which also
  // meant the browser never showed its title — the word was there and could
  // not be reached (Freddi, 2026-10-06: "see full status on hover"). A span
  // takes the hover, shows the word, and a click on it still reaches the
  // node button underneath, since nothing here is disabled.
  if (glyph && !interactive) {
    return (
      <span
        className={'status-pill ' + info.tone + (className ? ' ' + className : '') + ' glyph'}
        role="img"
        title={title ?? info.label}
        aria-label={info.label}
      >
        <Icon name={info.icon} size={13} className="status-ico" />
      </span>
    );
  }
  return (
    <button
      className={
        'status-pill ' + info.tone + (className ? ' ' + className : '') + (glyph ? ' glyph' : '') + (disabled && interactive ? ' is-busy' : '')
      }
      onClick={
        onClick && !disabled
          ? (e) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      // A glyph has no visible word, so the title carries it — the one place
      // a pointer can still read the status in full.
      title={title ?? (interactive ? 'Click to change status' : glyph ? info.label : '')}
      aria-label={glyph ? info.label : undefined}
      disabled={off}
      aria-disabled={off}
    >
      <Icon name={info.icon} size={13} className="status-ico" />
      {/* The word is its own element so a narrow desk can fold it away and
          leave the glyph, which carries the same meaning in a third of the
          width. Nothing else in the app does that — the class is the hook. */}
      {!glyph && <span className="status-pill-word">{info.label}</span>}
    </button>
  );
}
